import { Component, DEFAULT_CURRENCY_CODE, Inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { ReceiptType } from '../../../../../core/enum/receipt-type.enum';
import { ContainerType } from '../../../../../core/enum/container-type.enum';
import { EkycStatus } from '../../../../../core/enum/ekyc-status.enum';
import { PaymentType } from '../../../../../core/enum/payment-type.enum';
import { AttachmentInfo } from '../../../../../core/models/attachment/attachment-info';
import { Campaign } from '../../../../../core/models/campaign/campaign';
import { InvestmentModel } from '../../../../../core/models/investment/investment.model';
import { Payment, WalletPayment } from '../../../../../core/models/payment/payment';
import { AccountService } from '../../../../../core/services/api/account.service';
import { CampaignService } from '../../../../../core/services/api/campaign.service';
import { WalletService } from '../../../../../core/services/api/mysec/wallet.service';
import { ErrorService } from '../../../../../core/services/error.service';
import { BlockUiService } from '../../../../../shared/services/blockUi/block-ui.service';
import { PopupService } from '../../../../../shared/services/popup/popup.service';
import { GtmDataLayerService } from '../../../../../core/services/gtm-data-layer.service';
import { InvestmentInfo } from '../../../../../core/models/investment/investment-info';
import { Wallet } from '../../../../../core/models/mysec/wallet';
import { Observable } from 'rxjs';
import { CheckoutOutcomeModal } from './checkout-outcome-modal/checkout-outcome-modal.component';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { WalletTransactionStatus } from '../../../../../core/enum/mysec/wallet-transaction-status.enum';

@Component({
    selector: 'app-choose-payment',
    templateUrl: './choose-payment.component.html',
    styleUrls: ['./choose-payment.component.scss'],
    standalone: false
})
export class ChoosePaymentComponent implements OnInit {
  slug: string;
  investmentId: number;
  tickerCode: string;
  campaign: Campaign;
  investment: InvestmentModel;
  investmentInfo: InvestmentInfo;
  amount?: number = null;
  refNo: string;
  continueLink: string;
  step: number = 1;
  walletData?: Wallet;
  originCheckoutInvestmentId?: number;
  difference: number = 1;
  differences = () =>{ this.difference = (this.amount??0)-(this.walletData?.withdrawableBalance??0); }

  isLoading: boolean = false;
  isLoadingInvestmentInfo: boolean = false;
  isPSTX: boolean = false;
  isDuitNowEnabled: boolean = false;

  receiptType: ReceiptType;
  attachmentInfo: AttachmentInfo;
  document: File;

  selectedPaymentType: PaymentType = PaymentType.MaybankFPX;

  ReceiptType: typeof ReceiptType = ReceiptType;
  PaymentType: typeof PaymentType = PaymentType;

  attachmentInfoList: AttachmentInfo[] = [];
  documents: (File | null)[] = [null];
  maxFiles = 5;


  constructor(
    private readonly modalService: NgbModal,
    private activatedRoute: ActivatedRoute,
    private popupService: PopupService,
    private campaignService: CampaignService,
    private errorService: ErrorService,
    private router: Router,
    private accountService: AccountService,
    private blockUiService: BlockUiService,
    private walletService: WalletService,
    private _gtmDataLayerService: GtmDataLayerService,
    @Inject(DEFAULT_CURRENCY_CODE) private defaultCurrencyCode: string,
  ) {}

  ngOnInit() {
    this.slug = this.activatedRoute.snapshot.params.slug;
    this.investmentId = Number.parseInt(this.activatedRoute.snapshot.params.investmentId, 10);
    this.amount = Number.parseFloat(this.activatedRoute.snapshot.params.amount);
    if (!Number.isFinite(this.amount)) this.amount = null;
    this.tickerCode = this.activatedRoute.snapshot.params.tickerCode;
    // Topup in midst of investment checkout
    if (this.slug && Number.isFinite(this.amount) && Number.isFinite(this.investmentId)) {
      this.originCheckoutInvestmentId = this.investmentId;
      this.receiptType = ReceiptType.Wallet;
      this.continueLink = `/equity/${this.slug}/invest-now/payment/${this.investmentId}`;
      this.isDuitNowEnabled = true;
      this.isPSTX = true;

      this.walletService.getWallet().subscribe({
        next: (data: Wallet) => {
          this.walletData = data;
          this.differences();
        },
        error: () => {},
      });
      this.getCampaign();

      return;
    }
    // Invest case
    if (this.slug && this.investmentId) {
      this.receiptType = ReceiptType.Investment;
      this.continueLink = `/equity/${this.slug}/thank-you`;
      this.getCampaign();
      this.walletService.getWallet().subscribe({next:(data:Wallet)=>{this.walletData=data; this.differences() },error:()=>{}});
      this.isPSTX = false;
    }
    // Basic topup case
    if (this.amount) {
      this.receiptType = ReceiptType.Wallet;
      this.continueLink = '/pstx/account';
      this.isDuitNowEnabled = true;
      this.isPSTX = true;
    }
  }

  logBeginCheckout() {
    if (this.step === 1) {
      this._gtmDataLayerService.logBeginCheckout({
        currency: this.defaultCurrencyCode.trim(),
        value: this.investment.pledgedAmount,
        items: [
          {
            item_name: this.campaign.company?.brandName,
            item_id: this.campaign.id,
            item_category: 'ecf',
            price: this.investmentInfo.pricePerShare * this.investmentInfo.sharePerLot,
            quantity: Number(this.investment.lotQuantity),
          },
        ],
      });
    }
  }

  getCampaign() {
    this.isLoadingInvestmentInfo = true;

    this.campaignService.getCampaign(this.slug).subscribe({
      next: (data) => {
        this.campaign = data;
        this.getInvestment();
      },
      error: (err) => {
        this.errorService.showError(err);
      },
    });
  }

  getInvestment() {
    this.campaignService
      .getInvestment(this.campaign.id, this.investmentId)
      .pipe(finalize(() => (this.isLoadingInvestmentInfo = false)))
      .subscribe({
        next: (data) => {
          this.investment = data;
          this.amount ??= this.investment.pledgedAmount;
          this.differences();
          this.refNo = this.investment.referenceNumber;
          this.isDuitNowEnabled = this.investment.paymentType === PaymentType.DuitNow;
          this.getCampaignInvestmentInfo();
        },
        error: (err) => {
          this.errorService.showError(err);
        },
      });
  }

  getCampaignInvestmentInfo() {
    this.campaignService.getCampaignInvestmentInfo(this.campaign.id).subscribe({
      next: (data) => {
        this.investmentInfo = data;
        this.logBeginCheckout();
      },
      error: (err) => {
        this.errorService.showError(err);
      },
    });
  }

  setPayment(paymentModel: WalletPayment & Partial<Payment>) {
    const manualPaymentTypes = [PaymentType.BankIn, PaymentType.Swift, PaymentType.Cheque];

    if (paymentModel && manualPaymentTypes.includes(paymentModel.paymentType) && !this.attachmentInfo) {
      this.popupService.alert('common.pleaseUploadReceipt');
      return;
    }

    this.blockUiService.open();
    let observable: Observable<any>;
    
    if (paymentModel && paymentModel.paymentType !== PaymentType.Wallet) {
      paymentModel.attachmentInfo = this.attachmentInfo;
      paymentModel.attachmentInfoList = this.attachmentInfoList;
      if (this.receiptType == ReceiptType.Wallet && this.tickerCode) paymentModel.tickerCode = this.tickerCode;
      // If deposit midst of investment checkout
      if (this.receiptType == ReceiptType.Wallet && this.investmentId) paymentModel.sourceInvestmentId = this.investmentId;
      
      observable = this.receiptType == ReceiptType.Investment
          ? this.campaignService.setInvestmentPayment(
              this.campaign.id,
              this.investmentId,
              paymentModel,
            )
          : this.walletService.depositWallet(this.amount, paymentModel);
    }
    else{
      // topup wallet with wallet is unsupported
      // require campaign, investment, paymentModel
      if (!this.campaign || !this.investment || !paymentModel) {
        this.blockUiService.close();
        this.errorService.showError('common.invalidRequest');
        return;
      }
      observable = this.campaignService.setInvestmentPayment(
        this.campaign.id,
        this.investmentId,
        paymentModel,
      );
    }
    observable.pipe(finalize(() => this.blockUiService.close())).subscribe({
      next:(data) => {
        if (data && data.fpxPaymentGatewayUrl) window.location.href = data.fpxPaymentGatewayUrl;
        else this.getProfile(data);
      },
      error:(err) => {
        this.errorService.showError(err);
      },
    });
  }

  private getProfile(data: any): void {
    this.accountService.profile(true).subscribe({
      next: (profile) => {
        if (![EkycStatus.Verified, EkycStatus.NotRequired].includes(profile.ekycStatus)) {
          this.popupService.alert('investment.userMustCompleteEkyc').then(() => {
            this.router.navigate(['/proceed-e-kyc']);
          });
          return;
        }

        if (this.receiptType === ReceiptType.Investment) {
          this.router.navigate(['/equity', this.slug, 'thank-you'], {
            queryParams: {
              // Provide both `ref` and `paymentReferenceNo` for compatibility
              ref: this.refNo,
              paymentReferenceNo: this.refNo,
              campaignStatus: this.campaign.campaignStatus,
            },
          });
          return;
        }
        if (this.originCheckoutInvestmentId) {
          const popupOptions = {
            size: 'lg',
            backdrop: 'static',
            centered: true,
            keyboard: false,
            windowClass: 'modal-full-screen'
          } as NgbModalOptions;
      
          const modalRef = this.modalService.open(CheckoutOutcomeModal, popupOptions);
          switch (data?.status) {
            case (WalletTransactionStatus.Approved):
            case (WalletTransactionStatus.FPXSuccessful):
              modalRef.componentInstance.setSuccess(true);
              break;
            default:
              modalRef.componentInstance.setSuccess(false);
              break;
          }
          modalRef.componentInstance.method = data?.paymentType !== undefined ? `paymentType.${PaymentType[data.paymentType as PaymentType]}` : '-';
          modalRef.componentInstance.amount = data?.amount ?? 0;
          if (modalRef.componentInstance.success) modalRef.componentInstance.balance = (this.walletData?.balance ?? 0) + (data?.amount ?? 0);
          modalRef.result.then(
            () => { this.router.navigate([this.continueLink]); },
            () => { this.router.navigate([this.continueLink]); },
          );
          return;
        }
        
        this.popupService
          .success('campaign.investmentWillBeConfirmed', {
            titleLabel: '',
            okLabel: 'mysec.viewBalance',
            cancelLabel: '',
            showTitle: true,
          })
          .then(() => {
            this.router.navigate([this.continueLink]);
          });
      },
      error: (err) => this.errorService.showError(err),
    });
  }

  nextStep(response: { isNext: boolean; paymentType: PaymentType }) {
    if (response.isNext) {
      this.step = 2;
      this.selectedPaymentType = response.paymentType;
    }
  }

  uploadReceipt(event: { file: File; index: number }) {
    const { file, index } = event;
    this.isLoading = true;

    this.accountService
      .uploadAttachmentPresignedURL(file, ContainerType.InvestmentReceipt)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (data) => {
          const info = new AttachmentInfo(data);
          this.attachmentInfo = info;
          this.document = file;
          this.onUploadSuccess(info, file, index);
        },
        error: (err) => {
          this.errorService.showError(err);
          this.attachmentInfo = null;
          this.attachmentInfoList = [];
          this.document = null;
          this.onUploadFailed(index);
        },
      });
  }

  onUploadSuccess(info: AttachmentInfo, file: File, index: number) {
  this.attachmentInfoList[index] = info;
    this.documents[index] = file;
    if (this.documents.length < this.maxFiles && !this.documents.includes(null)) {
      this.documents.push(null);
    }
  }

  onUploadFailed(index: number) {
    this.attachmentInfoList.splice(index, 1);
    this.documents[index] = null;
  
    if (this.documents.length === 0) {
      this.documents.push(null);
    }
  }
}
